/**
 * Auth Store -- Xyden Leadership Assessment Platform
 * Step 6F Green Phase (Zustand)
 *
 * Note: Tests mock this store via vi.mock('@/stores/authStore').
 * This implementation is used in production mode.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';

interface AuthStoreState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** True while the initial /api/auth/me check is in progress */
  isAuthLoading: boolean;
  /** True once the initial auth check has completed (success or failure) */
  isAuthResolved: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setAuthResolved: (resolved: boolean) => void;
  setAuthLoading: (loading: boolean) => void;
  /** Reset store to initial state -- used by AppProviders on mount for test isolation */
  reset: () => void;
}

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isAuthLoading: false,
  isAuthResolved: false,
};

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      login: async (email: string, password: string) => {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        if (res.ok) {
          const data = (await res.json()) as { token: string; user: User };
          set({ user: data.user, token: data.token, isAuthenticated: true });
        } else {
          let message = 'Login failed';
          try {
            const body = await res.json() as { detail?: { message?: string; error?: string } | string; message?: string; error?: string };
            const detail = body.detail;
            if (detail && typeof detail === 'object' && detail.message) message = detail.message;
            else if (detail && typeof detail === 'string') message = detail;
            else if (body.message) message = body.message;
            else if (body.error) message = body.error;
          } catch { /* keep default */ }
          throw new Error(message);
        }
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: user !== null });
      },

      setToken: (token: string | null) => {
        set({ token });
      },

      setAuthResolved: (resolved: boolean) => {
        set({ isAuthResolved: resolved });
      },

      setAuthLoading: (loading: boolean) => {
        set({ isAuthLoading: loading });
      },

      reset: () => {
        set({
          user: null,
          isAuthenticated: false,
          isAuthLoading: false,
          isAuthResolved: false,
          // token intentionally excluded — persisted in localStorage, cleared only by logout()
        });
      },
    }),
    {
      name: 'xyden-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
